import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useMenuCollapse } from '@jwp/ott-ui-react/src/hooks/useMenuCollapse';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { unicodeToChar } from '@jwp/ott-common/src/utils/common';
import { determinePath } from '@jwp/ott-common/src/utils/urlFormatting';
import env from '@jwp/ott-common/src/env';
import { useUIStore } from '@jwp/ott-common/src/stores/UIStore';
import { useTranslationKey } from '@jwp/ott-hooks-react/src/useTranslationKey';
import { useMenuItems } from '@jwp/ott-hooks-react/src/useMenuItems';

import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import HeaderMenu from '../../components/Header/HeaderMenu';
import HeaderNavigation from '../../components/Header/HeaderNavigation';
import HeaderBrand from '../../components/Header/HeaderBrand';
import HeaderActions from '../../components/Header/HeaderActions';
import HeaderSkipLink from '../../components/Header/HeaderSkipLink';
import SidebarContainer from '../SidebarContainer/SidebarContainer';
import SiteMetadata from '../SiteMetadata/SiteMetadata';
import HeaderSearch from '../HeaderSearch/HeaderSearch';
import HeaderLanguageSwitcher from '../HeaderLanguageSwitcher/HeaderLanguageSwitcher';
import HeaderUserMenu from '../HeaderUserMenu/HeaderUserMenu';

import styles from './Layout.module.scss';

const Layout = () => {
  const { t } = useTranslation('common');
  const translationKey = useTranslationKey('label');
  const { data: menu = [] } = useMenuItems();

  const { config } = useConfigStore(
    ({ config, accessModel, supportedLanguages }) => ({
      config,
      accessModel,
      supportedLanguages,
    }),
    shallow,
  );
  const { assets, siteName } = config;
  const footerText = unicodeToChar(env.APP_FOOTER_TEXT);
  const navItems = [
    { label: t('home'), to: '/' },
    ...menu.map(({ label, contentId, type, custom }) => ({
      label: custom?.[translationKey] || label,
      to: determinePath({ type, contentId, label }),
    })),
  ];

  const showHeaderMenu = useMenuCollapse(navItems.length);
  const { sideBarOpen, searchActive, cookieWallOpen, hideFooter } = useUIStore((state) => ({
    sideBarOpen: state.sideBarOpen,
    searchActive: state.searchActive,
    cookieWallOpen: state.cookieWallOpen,
    hideFooter: state.hideFooter,
  }));
  const banner = assets.banner;
  const showFooter = !!footerText && !hideFooter;

  const openSideBar = () => useUIStore.setState({ sideBarOpen: true });

  return (
    <div className={styles.layout}>
      <SiteMetadata />
      <div inert={sideBarOpen || cookieWallOpen}>
        <Header searchActive={searchActive}>
          <HeaderSkipLink />
          {showHeaderMenu && <HeaderMenu sideBarOpen={sideBarOpen} onClick={openSideBar} />}
          <HeaderBrand siteName={siteName} logoSrc={banner} setLogoLoaded={() => undefined} />
          {!showHeaderMenu && <HeaderNavigation navItems={navItems} />}
          <HeaderActions>
            <HeaderSearch />
            <HeaderLanguageSwitcher />
            <HeaderUserMenu />
          </HeaderActions>
        </Header>
        <main id="content" className={styles.main} tabIndex={-1}>
          <Outlet />
        </main>
        {showFooter && <Footer text={footerText} />}
      </div>
      <SidebarContainer />
    </div>
  );
};

export default Layout;
